import React, { useEffect, useState } from "react";
import {
  AlertTriangle, FileText, Truck, HardHat, ShieldCheck, Wrench,
  CheckCircle2, FolderTree,
} from "lucide-react";

/**
 * LiveHeartbeatTicker — thin operational event strip pinned to the top
 * of /demo. Cycles through real-feeling field events (Daily Report
 * submitted · Pre-Op FAIL · Toolbox completed · Equipment locked out ·
 * etc.) on a 3-second rotation with cross-fade.
 *
 * Pure visual — uses scripted events to guarantee a steady, mission-
 * critical heartbeat regardless of API state. Stylistically restrained:
 * single line, mono caps, no flash.
 */
const EVENTS = [
  { icon: FileText,      kind: "DAILY REPORT",      detail: "FO-1003 · I-95 MOT", to: "PM Folder · Job Folder · Backup", sev: "info" },
  { icon: AlertTriangle, kind: "PRE-OP FAIL",        detail: "EX-210 · brake check FAIL", to: "Shop Queue · Safety · LOCKED OUT", sev: "critical" },
  { icon: HardHat,       kind: "TOOLBOX TALK",       detail: "Trench safety · 9 attendees", to: "Safety Folder · Crew records", sev: "info" },
  { icon: Truck,         kind: "EQUIPMENT OOS",      detail: "LD-544 · ST-12 · awaiting clearance", to: "Shop · PM notified", sev: "critical" },
  { icon: ShieldCheck,   kind: "INCIDENT FILED",     detail: "Near-miss · Pinch point · Sta 245+00", to: "Safety · PM · Admin", sev: "warning" },
  { icon: CheckCircle2,  kind: "QA/QC PASS",         detail: "Concrete pour · 42 CY · 4000 PSI", to: "PM · Admin · Test-Lab cc", sev: "success" },
  { icon: FolderTree,    kind: "PM ALERT",           detail: "Weather delay · 1.4 hr lost", to: "PM · Superintendent · Admin", sev: "warning" },
  { icon: Wrench,        kind: "PARTS REQUEST",      detail: "EX-210 · stabilizer ram seal", to: "Shop · Vendor · approval queue", sev: "info" },
  { icon: FileText,      kind: "DAILY REPORT",       detail: "FO-1001 · SR-22 widening", to: "PM Folder · Job Folder · Backup", sev: "info" },
  { icon: ShieldCheck,   kind: "JHP ACK",            detail: "Crew 3 · 6 signatures · digital", to: "Safety · Crew records · Job Folder", sev: "success" },
];

const SEV = {
  info:     { color: "#FF6A00", dot: "#FF6A00" },
  success:  { color: "#65DA76", dot: "#2E7D32" },
  warning:  { color: "#FFD000", dot: "#FFB000" },
  critical: { color: "#FF8A8A", dot: "#D32F2F" },
};

export default function LiveHeartbeatTicker() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % EVENTS.length);
    }, 3200);
    return () => clearInterval(t);
  }, []);

  const e = EVENTS[idx];
  const sev = SEV[e.sev] || SEV.info;
  const Icon = e.icon;

  return (
    <div
      className="border-b"
      style={{
        background: "#070707",
        borderColor: "#1A1A1A",
      }}
      data-testid="live-heartbeat-ticker"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-9 flex items-center gap-3 sm:gap-5 overflow-hidden">
        {/* LIVE indicator */}
        <span className="inline-flex items-center gap-1.5 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] shrink-0" style={{ color: sev.color }}>
          <span className="inline-flex w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: sev.dot }} />
          LIVE
        </span>

        <span className="hidden sm:inline-block w-px h-4 shrink-0" style={{ background: "#2A2A2A" }} />

        {/* Cycling event — cross-faded by key */}
        <div
          key={idx}
          className="flex-1 min-w-0 flex items-center gap-2 sm:gap-3 animate-fadein"
        >
          <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" style={{ color: sev.color }} />
          <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.25em] shrink-0" style={{ color: sev.color }}>
            {e.kind}
          </span>
          <span className="font-mono text-[10px] text-white truncate">
            {e.detail}
          </span>
          <span className="hidden md:inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] shrink-0" style={{ color: "#8A8A8A" }}>
            <span style={{ color: "#FF6A00" }}>→</span>
            <span className="truncate" style={{ color: "#BDBDBD" }}>{e.to}</span>
          </span>
        </div>

        {/* Step indicators */}
        <div className="hidden sm:flex items-center gap-1 shrink-0">
          {EVENTS.map((_, i) => (
            <span
              key={i}
              className="block w-1 h-1 rounded-full"
              style={{
                background: i === idx ? sev.color : "#2A2A2A",
                opacity: i === idx ? 1 : 0.5,
              }}
            />
          ))}
        </div>

        <span className="hidden lg:inline-block font-mono text-[9px] uppercase tracking-[0.25em] shrink-0" style={{ color: "#8A8A8A" }}>
          Auto-routing · 24/7
        </span>
      </div>
    </div>
  );
}
